import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocale } from '../../i18n'
import { useNavigate } from 'react-router-dom'
import { getTableroCapsulas, detenerCapsula } from '../../api/client'
import { useAsignar } from './useAsignar'
import { useReloj } from './useReloj'
import { duracion, hora, segundosVivos, enColacion } from './tiempo'
import RegistrarModal from './RegistrarModal'
import PersonaTrabajo from './PersonaTrabajo'
import { usePuedeAsignarPartes } from '../../utils/mantenimiento'

// The maintenance lead's board: everyone on the left with what they're on
// now, every open parte on the right with who's on it. Assign by dragging a
// person onto a parte, or with a person's "Asignar" button. Moving someone
// to another parte pauses what they were doing (the server does it).
// Without Mantenimiento editing it is only a view: no assigning or pausing.
export default function TableroAsignaciones() {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeAsignarPartes()
  const navigate = useNavigate()
  const ahora = useReloj()
  const [datos, setDatos] = useState(null)
  const [recibido, setRecibido] = useState(0)
  const [error, setError] = useState('')
  const [buscar, setBuscar] = useState('')
  const [eligiendo, setEligiendo] = useState(null)      // persona whose parte picker is open
  const [sobre, setSobre] = useState(null)               // parte id a person is dragged over
  const [registrando, setRegistrando] = useState(false)
  const [viendo, setViendo] = useState(null)             // persona id whose panel is open

  const cargar = useCallback(() => {
    getTableroCapsulas()
      .then(d => { setDatos(d); setRecibido(Date.now()); setError('') })
      .catch(() => setError(t('capsulas.cargaFallo')))
  }, [t])
  useEffect(() => {
    cargar()
    const id = setInterval(cargar, 60000)      // catches the automatic end-of-jornada stops
    return () => clearInterval(id)
  }, [cargar])

  const { asignar, ui } = useAsignar({ onHecho: cargar })
  const pausar = async (c) => { try { await detenerCapsula(c.id) } finally { cargar() } }

  if (error && !datos) return <p className="text-red-500 text-sm">{error}</p>
  if (!datos) return <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>

  const b = buscar.trim().toLowerCase()
  const partes = datos.partes.filter(p => !b || `#${p.numero} ${p.unidad_codigo} ${p.nombre} ${p.ubicacion || ''}`.toLowerCase().includes(b))
  const abiertos = new Set(datos.partes.map(p => p.id))
  const trabajando = datos.personas.filter(p => p.abierta).length

  const soltar = (e, parte) => {
    e.preventDefault()
    setSobre(null)
    const persona = datos.personas.find(p => String(p.id) === e.dataTransfer.getData('text/persona'))
    if (persona) asignar(persona, parte)
  }

  return (
    <div className="space-y-4">
      <Jornada datos={datos} trabajando={trabajando} recibido={recibido} ahora={ahora} t={t}
        onRegistrar={puedeEditar ? () => setRegistrando(true) : null} />
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="grid gap-5 lg:grid-cols-[minmax(300px,360px)_1fr] items-start">
        {/* People */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-1">
            {t('capsulas.personas')} · {t('capsulas.nTrabajando', { n: trabajando, total: datos.personas.length })}
          </h3>
          {datos.personas.map(p => (
            <TarjetaPersona key={p.id} p={p} recibido={recibido} ahora={ahora} t={t} puedeEditar={puedeEditar}
              reanudable={p.ultima && !p.abierta && abiertos.has(p.ultima.parte_id)}
              onVer={() => setViendo(p.id)}
              onAsignar={() => setEligiendo(e => (e === p.id ? null : p.id))}
              onPausar={() => pausar(p.abierta)}
              onReanudar={() => asignar(p, { id: p.ultima.parte_id, numero: p.ultima.parte_numero })}>
              {eligiendo === p.id && (
                <SelectorParte partes={datos.partes} actual={p.abierta?.parte_id} t={t}
                  onElegir={parte => { setEligiendo(null); asignar(p, parte) }}
                  onClose={() => setEligiendo(null)} />
              )}
            </TarjetaPersona>
          ))}
          {!datos.personas.length && (
            <p className="text-sm text-gray-400 px-1">
              {t('capsulas.sinPersonas')}{' '}
              <button type="button" onClick={() => navigate('/admin/personas')} className="underline">{t('capsulas.irPersonas')}</button>
            </p>
          )}
          {puedeEditar && <p className="text-[11px] text-gray-400 px-1 pt-1">{t('capsulas.arrastraHint')}</p>}
        </section>

        {/* Open partes */}
        <section className="space-y-2 min-w-0">
          <div className="flex items-center justify-between gap-3 px-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('capsulas.partesAbiertos', { n: datos.partes.length })}
            </h3>
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('capsulas.buscarParte')}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-64 bg-white" />
          </div>
          <div className="space-y-2">
            {partes.map(parte => (
              <div key={parte.id}
                {...(puedeEditar && {
                  onDragOver: e => { e.preventDefault(); setSobre(parte.id) },
                  onDragLeave: () => setSobre(s => (s === parte.id ? null : s)),
                  onDrop: e => soltar(e, parte),
                })}
                className={`rounded-xl border bg-white px-4 py-3 transition-colors ${
                  sobre === parte.id ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50/40'
                    : parte.trabajando.length ? 'border-emerald-200' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <button type="button" onClick={() => navigate(`/operations/partes?parte=${parte.id}`)}
                    className="text-left min-w-0 group" title={t('capsulas.verParte')}>
                    <span className="text-sm font-semibold text-gray-900 group-hover:underline">#{parte.numero}</span>
                    <span className="ml-2 text-sm font-mono text-gray-600">{parte.unidad_codigo}</span>
                    <span className="ml-2 text-sm text-gray-700">{parte.nombre}</span>
                    {parte.ubicacion && (
                      <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600 align-middle">{parte.ubicacion}</span>
                    )}
                  </button>
                  {puedeEditar && (
                    <AgregarPersona personas={datos.personas.filter(p => p.abierta?.parte_id !== parte.id)} t={t}
                      onElegir={persona => asignar(persona, parte)} />
                  )}
                </div>
                {parte.trabajando.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {parte.trabajando.map(c => (
                      <span key={c.id} className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-0.5 text-xs ${
                        enColacion(c, recibido, ahora) ? 'bg-gray-100 text-gray-600'
                          : c.hora_extra ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'}`}>
                        <span className="font-medium">{c.persona_nombre}</span>
                        <span className="tabular-nums opacity-75">
                          {enColacion(c, recibido, ahora) ? t('capsulas.retomaCorto', { hora: hora(c.inicio) }) : duracion(segundosVivos(c, recibido, ahora))}
                        </span>
                        {c.hora_extra && <span title={t('capsulas.autorizadaPor', { por: c.autorizada_por })}>HE</span>}
                        {puedeEditar && (
                          <button type="button" onClick={() => pausar(c)} title={t('capsulas.pausar')}
                            className="w-4 h-4 rounded-full hover:bg-black/10 leading-none">×</button>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-1.5">{t('capsulas.nadie')}</p>
                )}
              </div>
            ))}
            {!partes.length && <p className="text-sm text-gray-400 text-center py-10">{t('capsulas.sinPartes')}</p>}
          </div>
        </section>
      </div>

      {registrando && (
        <RegistrarModal personas={datos.personas} partes={datos.partes}
          onClose={() => setRegistrando(false)} onHecho={cargar} />
      )}
      {viendo != null && (
        <PersonaTrabajo key={viendo} personaId={viendo} onClose={() => { setViendo(null); cargar() }} />
      )}
      {ui}
    </div>
  )
}

function Jornada({ datos, trabajando, recibido, ahora, onRegistrar, t }) {
  const j = datos.jornada_hoy
  // Where the clock is now, from the server's time when the data arrived.
  const ya = new Date(Date.parse(datos.ahora) + (ahora - recibido))
  const enCol = j?.colacion_inicio && ya >= new Date(Date.parse(j.colacion_inicio)) && ya < new Date(Date.parse(j.colacion_fin))
  const hoy = new Date(`${datos.ahora.slice(0, 10)}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', day: 'numeric', month: 'long' })
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
      datos.en_jornada || enCol ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'}`}>
      <div className="text-sm">
        <span className="font-semibold text-gray-900 capitalize">{hoy}</span>
        <span className="text-gray-500"> · {j ? t('capsulas.jornada', { inicio: hora(j.inicio), fin: hora(j.fin) }) : t('capsulas.noLaboralHoy')}</span>
        {j?.colacion_inicio && (
          <span className="text-gray-500"> · {t('capsulas.colacion', { inicio: hora(j.colacion_inicio), fin: hora(j.colacion_fin) })}</span>
        )}
        {enCol
          ? <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">{t('capsulas.enColacion')}</span>
          : datos.en_jornada
          ? <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">{t('capsulas.enJornada')}</span>
          : <span className="ml-2 text-xs text-amber-800">{t('capsulas.fueraJornada')}</span>}
        {trabajando > 0 && <span className="ml-2 text-xs text-gray-500">{t('capsulas.nTrabajandoCorto', { n: trabajando })}</span>}
      </div>
      {onRegistrar && (
        <button type="button" onClick={onRegistrar}
          className="text-sm font-medium text-gray-700 border border-gray-200 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50">
          {t('capsulas.registrar.boton')}
        </button>
      )}
    </div>
  )
}

function TarjetaPersona({ p, recibido, ahora, reanudable, puedeEditar, onVer, onAsignar, onPausar, onReanudar, t, children }) {
  const c = p.abierta
  return (
    <div draggable={puedeEditar}
      onDragStart={puedeEditar ? e => { e.dataTransfer.setData('text/persona', String(p.id)); e.dataTransfer.effectAllowed = 'move' } : undefined}
      className={`relative rounded-xl border bg-white px-3.5 py-3 ${puedeEditar ? 'cursor-grab active:cursor-grabbing' : ''} ${
        c && !enColacion(c, recibido, ahora) ? (c.hora_extra ? 'border-amber-300' : 'border-emerald-300') : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button type="button" onClick={onVer} className="font-semibold text-gray-900 hover:text-indigo-600 hover:underline"
            title={t('capsulas.verTrabajo')}>{p.nombre}</button>
          {p.cargo && <span className="ml-1.5 text-xs text-gray-400">{p.cargo}</span>}
        </div>
        <span className="text-[11px] text-gray-400 shrink-0" title={t('capsulas.hoyTotal')}>
          {t('capsulas.hoy')} {duracion(p.segundos_hoy + (c ? segundosVivos(c, recibido, ahora) - c.segundos : 0))}
        </span>
      </div>
      <div className="mt-1 text-xs">
        {c && enColacion(c, recibido, ahora) ? (
          <p className="text-gray-600">
            ◐ {t('capsulas.retoma', { hora: hora(c.inicio), numero: c.parte_numero, unidad: c.unidad_codigo || '' })}
            <span className="block text-gray-500 truncate">{c.parte_nombre}</span>
          </p>
        ) : c ? (
          <p className={c.hora_extra ? 'text-amber-800' : 'text-emerald-700'}>
            ● {t('capsulas.trabajandoEn', { numero: c.parte_numero, unidad: c.unidad_codigo || '' })}
            <span className="ml-1 font-semibold tabular-nums">{duracion(segundosVivos(c, recibido, ahora))}</span>
            {c.hora_extra && <span className="ml-1">({t('capsulas.horaExtra')})</span>}
            <span className="block text-gray-500 truncate">{c.parte_nombre} · {t('capsulas.desde', { hora: hora(c.inicio) })}</span>
          </p>
        ) : p.ultima ? (
          <p className="text-gray-500">{t('capsulas.enPausa', { numero: p.ultima.parte_numero, hora: hora(p.ultima.fin) })}</p>
        ) : (
          <p className="text-gray-400">{t('capsulas.libre')}</p>
        )}
      </div>
      {puedeEditar && <div className="flex gap-2 mt-2">
        <button type="button" onClick={onAsignar}
          className="text-xs font-medium bg-gray-900 text-white rounded-md px-2.5 py-1 hover:bg-gray-800">
          {c ? t('capsulas.cambiar') : t('capsulas.asignar')}
        </button>
        {c && (
          <button type="button" onClick={onPausar}
            className="text-xs font-medium border border-gray-200 rounded-md px-2.5 py-1 text-gray-700 hover:bg-gray-50">
            {t('capsulas.pausar')}
          </button>
        )}
        {reanudable && (
          <button type="button" onClick={onReanudar}
            className="text-xs font-medium border border-gray-200 rounded-md px-2.5 py-1 text-gray-700 hover:bg-gray-50">
            {t('capsulas.reanudar', { numero: p.ultima.parte_numero })}
          </button>
        )}
      </div>}
      {children}
    </div>
  )
}

// A person's "Asignar": pick one of the open partes (searchable).
function SelectorParte({ partes, actual, onElegir, onClose, t }) {
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [onClose])
  const b = q.trim().toLowerCase()
  const lista = partes.filter(p => p.id !== actual && (!b || `#${p.numero} ${p.unidad_codigo} ${p.nombre}`.toLowerCase().includes(b)))
  return (
    <div ref={ref} className="absolute left-3 right-3 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-lg p-2">
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('capsulas.buscarParte')}
        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm mb-1.5" />
      <ul className="max-h-64 overflow-y-auto">
        {lista.map(p => (
          <li key={p.id}>
            <button type="button" onClick={() => onElegir(p)}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm">
              <span className="font-semibold">#{p.numero}</span>
              <span className="ml-1.5 font-mono text-gray-600">{p.unidad_codigo}</span>
              <span className="ml-1.5 text-gray-600 truncate">{p.nombre}</span>
              {p.trabajando.length > 0 && <span className="ml-1.5 text-[11px] text-emerald-700">({p.trabajando.length})</span>}
            </button>
          </li>
        ))}
        {!lista.length && <li className="text-xs text-gray-400 px-2 py-2">{t('capsulas.sinPartes')}</li>}
      </ul>
    </div>
  )
}

// A parte's "+ Persona": pick someone not already on it.
function AgregarPersona({ personas, onElegir, t }) {
  return (
    <select value="" onChange={e => { const p = personas.find(x => String(x.id) === e.target.value); if (p) onElegir(p) }}
      className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-600 shrink-0">
      <option value="">{t('capsulas.agregarPersona')}</option>
      {personas.map(p => (
        <option key={p.id} value={p.id}>
          {p.nombre}{p.abierta ? ` (${t('capsulas.enParte', { numero: p.abierta.parte_numero })})` : ''}
        </option>
      ))}
    </select>
  )
}
